// One pull request: metadata, threads, timeline, checks, files, attention,
// marks and the viewer's pending review. Shared by the PR's views while any
// of them is mounted (acquirePrStore / releasePrStore); the last release
// closes its live subscription and drops it, once its writes are done.
//
// What it loads is a runtime Program (pr/program.ts): each read runs in a
// lane, its state is a `Remote`, and the refetch owed after a rate limit is
// a retry the runtime armed. Its writes are sent as effects of the Program
// too (pr/outbox.ts), and are made by pr/draftWrites.ts (drafts),
// pr/prWrites.ts (GitHub and attention) and pr/publish.svelte.ts (publish
// and hand-off). This class is the store's shell: it shows the Program's
// state with the pending writes laid over it, sends it messages, holds the
// live subscription, and hands each write to the module that makes it.
import type { KnownRef } from '../logic/links';
import { rateLimitedUntil } from '../api/gh';
import { onIdentityChange } from '../api/identity';
import { appLog } from '../runtime/appLog';
import { total } from '../runtime/total';
import type { CheckContext, PrFile, PullRequestData, RepositoryData, ReviewThread, TimelineItem } from '../api/queries';
import { copyIn, selfMember, stackMenu, type StackMenu } from '../logic/prStack';
import { pullTrack, type ReplacedLookup } from '../logic/pullTrack';
import { emptyTrack, type Track } from '../logic/track';
import type { PullSubject } from '../subject';
import type { PullHost } from './rangeHost';
import { getAttention, getDrafts, orgPolicy, type DiscardedOnGitHub, type DraftTarget, type OrgPolicy, type ReviewDraft, type ServerOverride } from '../api/server';
import { answeredBy, isIncluded, mirroredComment, publishPlan, withShownStates, type PublishPlan } from '../logic/inbox';
import { LiveEvents, pausedForRateLimit, shouldRefetch, type Notice, type RevokeReason, type Trigger } from '../api/sse';
import type { AttentionMember } from '../engine/types';
import { latestEventAt, snapshotsFrom, toTimelineEvents, type Snapshot } from '../logic/timeline';
import { describeWriteError, installUrl, isNotInstalled, readOnlyText, type WriteError } from '../api/errors';
import { explicitAccess, repoCoverage, type Coverage } from '../api/install';
import {
  canSetAttention,
  canSetOthers,
  participantsOf,
  type AttentionChange,
  type AttentionWriter,
  type Participant,
} from '../logic/attention';
import type { Aftermath } from '../logic/aftermath';
import type { Failure } from '../api/failure';
import { Program } from '../runtime/effects';
import { manual, map as mapRemote, valueOf, type Remote, type Retry } from '../runtime/remote';
import { Writes, type Confirm, type WriteSpec } from './pending.svelte';
import { draftIdOf, withDraftThreads, withoutMirrored, type PrState, type ReviewEvent, type Subject } from './optimistic';
import { MarksStore, type MarksNotice } from './marks.svelte';
import { acquireNotes, type NotesStore } from './notes.svelte';
import { asideIds, planAside } from '../review/asides';
import { PrPrefs, prefs } from './prefs.svelte';
import { ViewedSync } from './viewedSync.svelte';
import { settings } from './settings.svelte';
import {
  idbBaselineStore,
  readViewedPages,
  VIEWED_WRITE_SPACING_MS,
  viewedImportRetryAt,
  viewedRefusal,
  viewedRefused,
  viewedRetryAt,
  writeViewed,
} from './githubViewed';
import { session } from './session.svelte';
import { headPushedAt, previews, type DeploymentCommit, type Preview } from '../logic/previews';
import { attentionOf } from './pr/attention';
import { fetchAnnotations, fetchChecks, fetchFiles, fetchGenerated, fetchPr, fetchReplaced, fetchStack, keptStack, message, type CheckAnnotation, type PrAt, type PrProgress } from './pr/fetch';
import { savedDashboardRows } from './dashCache';
import { stackHold } from './stackHold';
import { DraftWrites } from './pr/draftWrites';
import type { AfterPublish, Draft, HandOffResult, HandOffWrite, NewHeld, NewThread, PublishResult, Reloaded, WriteHost } from './pr/host';
import { filesSteps, prSteps, type LoadView } from '../loading/steps';
import type { PrTab } from '../router';
import { sideReads } from './pr/kept';
import { Outbox } from './pr/outbox';
import { PrWrites } from './pr/prWrites';
import { Publishing } from './pr/publish.svelte';
import { failureIn, initialState, makeUpdate, serverData, type Msg, type Out, type PrLoads, type PrWorld } from './pr/program';

export type { ReviewEvent, Subject } from './optimistic';
export { subject } from './optimistic';
export { CHECKS_COALESCE_MS } from './pr/program';

export type { AfterPublish, Draft, HandOffResult, NewHeld, NewThread, PublishResult } from './pr/host';

const NO_REFS: KnownRef[] = [];
const NO_PREVIEWS: Preview[] = [];
const NO_DEPLOYMENTS: DeploymentCommit[] = [];

export class PrStore implements PullHost {
  /** What the page loaded, and how each load stands (pr/program.ts): the Program's state, as views read it. */
  private st: PrLoads = $state.raw(initialState());
  private readonly program: Program<PrLoads, Msg>;
  /** The Program's last list of things to do, once done (see `hear`). */
  private handled: readonly Out[] = [];

  /** Put `patch` in place of what the Program holds (a write's own knowledge, or a test's). */
  private put(patch: Partial<Pick<PrLoads, 'server' | 'viewer' | 'repository' | 'access' | 'unrecorded'>>) {
    this.st = { ...this.st, ...patch };
    this.program.send({ t: 'set', patch });
  }

  get repository(): RepositoryData | null {
    return this.st.repository;
  }
  set repository(repository: RepositoryData | null) {
    this.put({ repository });
  }
  get viewer(): string {
    return this.st.viewer;
  }
  set viewer(viewer: string) {
    this.put({ viewer });
  }
  /**
   * The PR as GitHub and the changelist server last sent it; loads write
   * it. Views read the fields below (`pr`, `threads`, …), which show it
   * with the pending writes applied.
   */
  get server(): PrState {
    return this.st.server;
  }
  set server(server: PrState) {
    this.put({ server });
  }
  /**
   * The writes in flight (shown at once, until confirmed), and the last one
   * that failed, with Retry. See pending.svelte.ts.
   */
  // Each write is sent as an effect of the Program (pr/outbox.ts), which records it in the account's log.
  readonly writes: Writes<PrState, Reloaded, Subject> = new Writes(
    (e) => this.writeError(e),
    (kind, send) => this.outbox.perform(kind, send),
  );
  private shown: PrState = $derived(this.writes.overlay(this.server));
  get pr(): PullRequestData | null {
    return this.shown.pr;
  }
  /**
   * The pull request's place in its stack, for the header's stack menu
   * (logic/prStack.ts): the stack the dashboard shows it in, native or
   * found by branches. It is read apart from the pull request (the
   * Program's `stack` lane), so it loads, fails and is tried again
   * (`reloadStack`) on its own; what the app already knew of it is there
   * before the pull request is, placed by the pool's own copy of it until
   * then. Null when the pull request is on its own. Once loaded, its own
   * row follows the page (a title edited here, checks read since).
   */
  stack: Remote<StackMenu | null> = $derived.by(() => {
    const pr = this.pr;
    const repository = this.st.repository;
    const me = this.st.viewer;
    return mapRemote(this.st.stack, (pool) => {
      if (!pool) return null;
      const self = pr && repository ? selfMember(pr, repository) : copyIn(pool, `${this.owner}/${this.repo}`, this.number);
      return self && stackMenu(pool, self, me);
    });
  });
  /**
   * Whether there is reason to think the pull request is in a stack before
   * its stack is read: it is based on a branch other than the repository's
   * default. The header says a stack is loading only then.
   */
  get stackExpected(): boolean {
    const base = this.pr?.baseRefName;
    const trunk = this.st.repository?.defaultBranchRef?.name;
    return !!base && !!trunk && base !== trunk;
  }
  /** Read the stack again from GitHub, alone. */
  reloadStack() {
    this.program.send({ t: 'stack' });
  }
  /** Review threads, with the viewer's drafts in them (see `withDraftThreads`). */
  get threads(): ReviewThread[] {
    return this.threadsWithDrafts;
  }
  get timeline(): TimelineItem[] {
    return this.shown.timeline;
  }
  /**
   * The viewer's review drafts, kept on the changelist server until
   * published, each in the state it is shown in: an included one is not
   * included any more once the pull request's head moves on from the one
   * it was included on (see `shownState`).
   */
  get drafts(): ReviewDraft[] {
    return this.shownDrafts;
  }
  /** The conversation drafts (comments on lines outside the diff, posted to the conversation on publish). */
  get held(): ReviewDraft[] {
    return this.shownDrafts.filter((d) => d.target.kind === 'conversation');
  }
  get overrides(): ServerOverride[] {
    return this.shown.overrides;
  }
  get attention(): AttentionMember[] {
    return this.shown.attention;
  }
  /** Older timeline items were dropped (more than the page cap). */
  get timelineTruncated(): boolean {
    return this.st.timelineTruncated;
  }
  get files(): PrFile[] | null {
    return valueOf(this.st.files)?.files ?? null;
  }
  /** Head SHA the PR pointed at when `files` was requested. */
  get filesHead(): string | null {
    return valueOf(this.st.files)?.head ?? null;
  }
  /** Why the changed files couldn't load, until a later load succeeds. */
  get filesFailure(): Failure | null {
    return failureIn(this.st.files)?.reason ?? null;
  }
  /**
   * The pull request shown is its first answer only, while the rest of its
   * threads, timeline and checks are read (see `PrLoads.prPartial`): what
   * counts them is still counting, and what needs all of them waits.
   */
  get partial(): boolean {
    return this.st.prPartial;
  }
  /** How far the load in flight is past its first answer (threads, timeline, checks read of their totals); null with none in flight. */
  get loadProgress(): PrProgress | null {
    return this.st.prProgress;
  }
  /**
   * The changed files a listing under way holds so far, provisional (not
   * yet confirmed against the head), the head it lists, and how many the
   * pull request says it changes; null with no listing under way.
   */
  get filesSoFar(): { files: readonly PrFile[]; head: string | null; total: number | null } | null {
    const s = this.st;
    if (!s.filesRun || !s.filesSoFar) return null;
    return { files: s.filesSoFar, head: s.filesRun.head, total: s.server.pr?.changedFiles ?? null };
  }
  /**
   * Settles once the listing under way holds more than `count` files, or
   * is over (answered, failed or superseded): the Files view grows as it
   * does.
   */
  filesGrew(count: number): Promise<void> {
    return this.when((s) => s.filesRun === null || (s.filesSoFar?.length ?? 0) > count);
  }
  get annotations(): Readonly<Record<string, CheckAnnotation[]>> {
    return this.st.annotations;
  }
  /** The organization's policy for this repository (see `OrgPolicy`: only `ready` grants anything). */
  get org(): OrgPolicy {
    const r = this.st.org;
    if (r.tag === 'Has') return r.value;
    return r.tag === 'Failed' ? { status: 'error', message: r.reason.message } : { status: 'loading' };
  }
  /** `revoked`: the viewer lost access to the repository while the page was open; nothing of it is shown any more. */
  get status(): 'loading' | 'ready' | 'error' | 'notfound' | 'revoked' {
    const s = this.st;
    if (s.revoked) return 'revoked';
    if (s.pr.tag === 'Has') return s.pr.value ? 'ready' : 'notfound';
    if (s.server.pr) return 'ready';
    return s.pr.tag === 'Failed' ? 'error' : 'loading';
  }
  /** The steps of the page's load as the view of `tab` waits on them (loading/steps.ts): what its loading screen shows. */
  loadView(tab: PrTab): LoadView {
    return prSteps(this.st, this, tab);
  }
  /** The steps the Files view waits on once the pull request is in. */
  get filesLoadView(): LoadView {
    return filesSteps(this.st, this);
  }
  /** Why the pull request couldn't load, or couldn't be read again; null otherwise. */
  get failure(): Failure | null {
    return failureIn(this.st.pr)?.reason ?? null;
  }
  /** How `failure` is tried again: by itself while the refetch at a rate limit's end is armed, else when the user asks. */
  get failureRetry(): Retry {
    return failureIn(this.st.pr)?.retry ?? manual;
  }
  /** The latest write still in flight (what disabled controls say), or null when none is. */
  get busy(): string | null {
    return this.writes.pending.at(-1)?.label ?? null;
  }
  /** Any write is in flight, or a review mark is being saved: the progress line under the top bar. */
  get saving(): boolean {
    return this.writes.active || this.marks.sending;
  }
  /** What the last publish left behind (see logic/aftermath): the verdict sent, a refusal, the hand-off's writes. */
  get aftermath(): Aftermath {
    return this.publishing.aftermath;
  }
  /** Why the last publish could not submit the review (shown on the Review view); cleared by the next publish, or by leaving the page. */
  get publishFailure(): WriteError | null {
    return this.aftermath.run.phase === 'refused' ? this.aftermath.run.error : null;
  }
  /**
   * Whether the changelist GitHub App can write to this repository for the
   * viewer (see `api/install`): checked up front when possible, and set to
   * `not_installed` when a write fails because of the App's access.
   */
  get access(): Coverage {
    return this.st.access;
  }
  set access(access: Coverage) {
    this.put({ access });
  }
  get generated(): (path: string) => boolean {
    return this.st.generated;
  }
  /** Drafts could not be loaded (e.g. the server does not offer them). */
  get heldUnavailable(): boolean {
    return failureIn(this.st.drafts) !== null;
  }
  /** Conversation drafts the last publish could not post: a live failure (Retry posts only those), until a publish starts. */
  get heldFailure() {
    return this.writes.failures.find((f) => f.kind === 'post-held') ?? null;
  }
  /**
   * Why the last publish could not post some conversation drafts: shown in
   * the Review view while one of them is still held, and until the next
   * publish attempt (the toast's Retry, `heldFailure`, may be gone sooner).
   */
  get heldNote(): WriteError | null {
    const u = this.publishing.heldUnposted;
    return u && this.server.drafts.some((h) => u.ids.includes(h.id)) ? u.error : null;
  }
  /**
   * Why comments of the viewer's pending review on GitHub are not on GitHub
   * after the last publish, while one of them is still kept here only.
   */
  get pendingNote(): WriteError | null {
    const u = this.publishing.pendingUnrestored;
    if (!u) return null;
    // With no draft named, it is the review's own summary: said until the next publish.
    return !u.ids.length || this.server.drafts.some((d) => u.ids.includes(d.id) && d.origin.kind === 'pendingReview' && !d.origin.on_github) ? u.error : null;
  }
  get heldError(): string | null {
    return this.heldNote?.text ?? null;
  }
  /**
   * Head SHA of the diff on screen, while the Files view shows one (see
   * `showDiff`). New comments anchor to this commit, so they land on the
   * lines shown even after the PR head moved; elsewhere they use the
   * current head.
   */
  diffHead: string | null = $state(null);
  private diffOwner: object | null = null;

  marks: MarksStore;
  /** The viewer's private notes on the repository's content, shared with every open page of the repository (see notes.svelte.ts `acquireNotes`). */
  notes: NotesStore;
  private releaseNotes: () => void;
  /** The review marks' sync with GitHub's per-file "Viewed" checkbox (see viewedSync.svelte.ts; the Files view's model drives its local side). */
  viewed: ViewedSync;
  /** Whether the last load of the pull request asked GitHub for the "Viewed" states. */
  private viewedRead = false;
  prefs: PrPrefs;
  private live: LiveEvents | null = null;
  private disposed = false;
  /** Disposed for good: the Program takes nothing more (a revoked store is `disposed`, and its Program still answers writes). */
  private stopped = false;
  private unhear: () => void;
  /** The writes' sends, as effects of the Program. */
  private readonly outbox: Outbox;
  /** The store's writes, by what they write (pr/draftWrites.ts, pr/prWrites.ts, pr/publish.svelte.ts). */
  private readonly drafting: DraftWrites;
  private readonly sending: PrWrites;
  private readonly publishing: Publishing;
  private tempSeq = 0;

  constructor(
    public owner: string,
    public repo: string,
    public number: number,
  ) {
    this.subject = { kind: 'pull', owner, repo, number };
    const at: PrAt = { owner, repo, number };
    const name = `${owner}/${repo}`;
    this.marks = new MarksStore(
      owner,
      repo,
      number,
      () => this.notInstalled(),
      () => appLog.marking(name, number),
    );
    ({ store: this.notes, release: this.releaseNotes } = acquireNotes(owner, repo, () => this.notInstalled()));
    // Opened: whatever the viewer does or sees here, the dashboard reads it afresh.
    appLog.opened(name, number);
    this.prefs = new PrPrefs(owner, repo, number);
    const repoKey = name.toLowerCase();
    this.viewed = new ViewedSync({
      write: (path, viewed, head) => {
        const id = this.server.pr?.id;
        if (!id) return Promise.reject(new Error('the pull request is not loaded'));
        return writeViewed(id, path, viewed, head);
      },
      store: idbBaselineStore(`${repoKey}#${number}`),
      spacingMs: VIEWED_WRITE_SPACING_MS,
      retryAt: (e, failures) => viewedRetryAt(e, failures),
      importRetryAt: (_e, failures) => viewedImportRetryAt(failures),
      refusal: viewedRefusal,
      // Refused for this repository: not tried again on its other pull requests until the page reloads.
      onPause: (reason) => viewedRefused.set(repoKey, reason),
      onHeadMoved: () => this.refreshSoon(true),
    });
    this.viewed.setEnabled(settings.viewedSync);
    const refused = viewedRefused.get(repoKey);
    if (refused) this.viewed.pause(refused);
    const side = sideReads(at);
    const world: PrWorld = {
      // GitHub's "Viewed" states come with the pull request while the sync is on, and while the user's choice is not
      // known yet (so learning it is on costs no second load); never while it is off.
      // How far a read is goes to the Program while its run is the lane's newest (a superseded run's signal is aborted).
      pr: (signal) => {
        this.viewedRead = settings.viewedSyncChoice !== false;
        return fetchPr(
          at,
          this.viewedRead,
          signal,
          (progress) => {
            if (!signal.aborted) this.program.send({ t: 'pr-progress', progress });
          },
          (loaded) => {
            if (!signal.aborted) this.program.send({ t: 'pr-partial', loaded });
          },
        );
      },
      viewedPages: (after, head, signal) =>
        readViewedPages(after, head, { owner, repo, number }, (states, readAt) => {
          if (!signal.aborted) this.viewed.remote({ head, at: readAt, states });
        }),
      checks: (signal) => fetchChecks(at, signal),
      files: (signal) =>
        fetchFiles(at, signal, (listed, files) => {
          if (!signal.aborted) this.program.send({ t: 'files-progress', listed, files });
        }),
      drafts: () => getDrafts(owner, repo, number),
      overrides: () => getAttention(owner, repo, number),
      attention: attentionOf,
      org: () => orgPolicy(owner, repo),
      // Answers are cached for the page's life (see `api/install`).
      access: async (repository) => (session.user ? repoCoverage(owner, repo, { explicitAccess: explicitAccess(repository) }) : 'unknown'),
      refs: side.refs,
      deployments: side.deployments,
      // What a page reads of its stack is held for the app: the next member's page starts from it.
      stackKept: async () => {
        const kept = await keptStack(at, stackHold.get(), savedDashboardRows);
        if (kept?.kind === 'stack') stackHold.put(name, kept.pool, kept.since);
        return kept;
      },
      stackFetch: async (signal) => {
        const pool = await fetchStack(at, signal);
        if (pool) stackHold.put(name, pool, Date.now());
        return pool;
      },
      replaced: (base, befores, signal) => fetchReplaced(at, base, befores, signal),
      annotations: (run, signal) => fetchAnnotations(at, run, signal),
      generated: (head, signal) => fetchGenerated(at, head, signal),
      marks: () => this.marks.load(),
      writing: (kind) => appLog.writing(name, number, kind),
      pausedUntil: () => rateLimitedUntil(),
      now: () => Date.now(),
      random: () => Math.random(),
    };
    // Another account signing in drops everything in flight; the page it leaves shows nothing of the last one.
    let outbox: Outbox | null = null;
    this.program = new Program<PrLoads, Msg>({
      init: () => {
        // Starting over: the writes in flight will never be answered.
        outbox?.abandon();
        return [initialState()];
      },
      update: makeUpdate(world),
      resetOn: onIdentityChange,
    });
    this.outbox = outbox = new Outbox(
      (m) => this.program.send(m),
      () => this.stopped,
    );
    this.unhear = this.program.subscribe((s) => this.hear(s));
    const store = this;
    const host: WriteHost = {
      owner,
      repo,
      number,
      writes: this.writes,
      byPrLoad: this.byPrLoad,
      get server() {
        return store.server;
      },
      get shown() {
        return store.shown;
      },
      get shownDrafts() {
        return store.shownDrafts;
      },
      get viewer() {
        return store.viewer;
      },
      get snapshots() {
        return store.snapshots;
      },
      get diffHead() {
        return store.diffHead;
      },
      get pendingReviewId() {
        return store.pendingReviewId;
      },
      get serverData() {
        return store.serverData;
      },
      get serverDataText() {
        return store.serverDataText;
      },
      get disposed() {
        return store.disposed;
      },
      get draftWrites() {
        return store.st.draftWrites;
      },
      get drops() {
        return store.st.drops;
      },
      get overridesReads() {
        return store.st.overridesReads;
      },
      write: (spec) => this.write(spec),
      tempId: () => `pending-${++this.tempSeq}`,
      me: () => this.me(),
      head: () => this.head(),
      commentCommit: () => this.commentCommit(),
      planFor: (event, body, drafts) => this.planFor(event, body, drafts),
      latestEventAt: () => this.latestEventAt(),
      notInstalled: () => this.notInstalled(),
      send: (m) => this.program.send(m),
      landDraft: (draft, since) => this.program.send({ t: 'draft-landed', draft, since }),
      dropDrafts: (ids) => this.program.send({ t: 'drafts-dropped', ids }),
      load: () => this.load(),
      loadDrafts: () => this.loadDrafts(),
      loadOverrides: () => this.loadOverrides(),
      flushMarks: () => this.marks.flush(),
    };
    this.drafting = new DraftWrites(host);
    this.publishing = new Publishing(host, {
      changeAttention: (changes, how) => this.sending.changeAttention(changes, how),
      requestReviewer: (login, how) => this.sending.requestReviewer(login, how),
    });
    // A write made by hand retires the hand-off's for the same login.
    this.sending = new PrWrites(host, (keys) => this.publishing.after({ kind: 'superseded', keys }));
  }

  /**
   * The Program's state arrived: show it and, in the same step, do what it
   * asks of the pending writes and the marks (`Out`), so a load and the
   * writes it confirms are never shown one without the other.
   */
  private hear(s: PrLoads) {
    this.st = s;
    if (!s.out.length || s.out === this.handled) return;
    this.handled = s.out;
    for (const o of s.out) {
      if (o.kind === 'settle') this.writes.settle(o.resource, o.basis);
      else if (o.kind === 'stall') this.writes.stall(o.resource, o.basis);
      else if (o.kind === 'viewed') this.viewed.remote(o.viewed);
      else if (o.kind === 'answered') o.settle(o.o);
      else {
        this.marks.forget();
        this.notes.forget();
        // The overrides are never fetched again: attention writes waiting on that are done.
        this.writes.settle('overrides', Infinity);
      }
    }
  }

  /** Settles once the Program's state satisfies `pred` (at once if it does), or the store stopped. */
  private when(pred: (s: PrLoads) => boolean): Promise<void> {
    return this.program.until(pred).then(
      () => {},
      () => {},
    );
  }

  /**
   * Settles once no read the Program keeps count of is in flight: the pull
   * request, its checks and files, and what each load reads beside it
   * (drafts, overrides and the attention set, access, org policy, related
   * titles, deployments, stack, annotations). Timers armed (a debounced
   * refresh, the checks' coalescing, a rate limit's resume) are not waited
   * for, nor are the marks, `.gitattributes`, "Viewed" pages or replaced
   * commits. Also settles once the store stopped. For tests and
   * diagnostics: what a load set off is done when it settles.
   */
  settled(): Promise<void> {
    return this.when(
      (s) =>
        s.prRun === null &&
        s.checksRun === null &&
        s.filesRun === null &&
        !s.draftsRun &&
        s.overridesDone >= s.overridesReads &&
        s.attentionDone >= s.attentionRuns &&
        s.accessDone >= s.accessRuns &&
        !s.orgRun &&
        !s.refsRun &&
        !s.deploymentsRun &&
        !s.stackRun &&
        !s.annotationsRun,
    );
  }

  /**
   * Settles once `pred` holds of the store (at once if it does now),
   * checked again each time the Program's state changes; rejects if the
   * store is disposed first. For tests: what a view would show next,
   * waited for by the change that shows it.
   */
  until(pred: (store: this) => boolean): Promise<void> {
    return this.program.until(() => pred(this)).then(() => {});
  }

  // ---------------------------------------------------------------- derived

  snapshots: Snapshot[] = $derived(this.pr ? snapshotsFrom(this.timeline, this.pr.headRefOid) : []);

  /** The pull request as a subject (subject.ts): what the range views are hosted for. */
  readonly subject: PullSubject;

  /** This store is the pull request's conversation capability (`PullHost.conversation`). */
  get conversation(): PrStore {
    return this;
  }

  /** The pull request's head commit, once loaded. */
  get headRefOid(): string | null {
    return this.pr?.headRefOid ?? null;
  }
  /** The base branch's head, as the pull request names it; null until it has loaded. */
  get baseRefOid(): string | null {
    return this.pr?.baseRefOid ?? null;
  }

  /** Lines added and removed base → head, as GitHub counts them. */
  get totals(): { added: number; removed: number } {
    return { added: this.pr?.additions ?? 0, removed: this.pr?.deletions ?? 0 };
  }

  /** The pull request is closed or merged: its head moves no more. */
  get closed(): boolean {
    const state = this.pr?.state;
    return state === 'CLOSED' || state === 'MERGED';
  }

  /**
   * The pull request's track (logic/pullTrack.ts): the commits the range
   * views draw and pick from. The replaced-commits lookup completes it
   * once `loadReplaced` has run.
   */
  track: Track = $derived(
    this.pr
      ? pullTrack(this.timeline, { headRefOid: this.pr.headRefOid, baseRefOid: this.pr.baseRefOid, baseRefName: this.pr.baseRefName, viewer: this.viewer, lookup: this.replaced })
      : emptyTrack(),
  );

  /** The drafts with the states they are shown in (see `drafts`). */
  private shownDrafts: ReviewDraft[] = $derived(withShownStates(this.shown.drafts, this.shown.pr?.headRefOid ?? ''));
  // The drafts as the Review view shows them (states included), so both agree after a push.
  /** GitHub's threads without the pending-review comments a draft mirrors: each of those is shown once, as its draft. */
  private githubThreads: ReviewThread[] = $derived(withoutMirrored(this.shown.threads, this.shownDrafts));
  private threadsWithDrafts: ReviewThread[] = $derived(withDraftThreads(this.githubThreads, this.shownDrafts, this.me()));

  /** Whether draft `id` was included but is shown as not, because the head has moved on since (see `shownState`). */
  droppedBack(id: ReviewDraft['id']): boolean {
    return this.dropped.has(id);
  }
  private dropped: Set<ReviewDraft['id']> = $derived(
    new Set(this.shownDrafts.filter((d, i) => d.state !== this.shown.drafts[i]?.state).map((d) => d.id)),
  );

  /**
   * The viewer's pending review on GitHub (made on github.com, or by a tool
   * through GitHub's API), if there is one. Changeling only reads it, and
   * mirrors its comments in the drafts (see `mirrorDrafts`); its own
   * drafts are in one only while a publish runs.
   */
  pendingReviewId: string | null = $derived(this.server.pr?.pending.nodes[0]?.id ?? null);

  /**
   * The viewer's pending review on GitHub as far as the pull request's own
   * read tells: which review, and how many comments. When it changes
   * (comments added or removed there, the review submitted or deleted),
   * the Review view reads the pending review again.
   */
  pendingSignature: string = $derived.by(() => {
    const p = this.server.pr?.pending.nodes[0];
    return p ? `${p.id}:${p.comments?.totalCount ?? 0}` : '';
  });

  /** Whether there is a pending review on GitHub to read, or a draft that mirrored one: the Review view then reads it. */
  mirrorsGitHub: boolean = $derived(this.pendingReviewId !== null || this.shownDrafts.some((d) => mirroredComment(d) !== null));

  /** Comments in the viewer's pending review on GitHub that no draft mirrors yet (it has not been read since they were written). */
  githubPendingCount: number = $derived(
    this.githubThreads.reduce((n, t) => n + t.comments.nodes.filter((c) => c.pullRequestReview?.state === 'PENDING').length, 0),
  );

  /** The drafts set aside (review/asides.ts: kept for the viewer, out of the review), by ID as a string. */
  asides: ReadonlySet<string> = $derived.by(() => asideIds(this.marks.flags));

  /** Drafts for the review: every draft but those set aside. */
  reviewDrafts: ReviewDraft[] = $derived(this.shownDrafts.filter((d) => !this.asides.has(String(d.id))));

  /** Drafts of every kind: those the Changeling server holds (but those set aside), and comments of a pending review on GitHub not mirrored yet. */
  draftCount: number = $derived(this.reviewDrafts.length + this.githubPendingCount);

  /** Drafts included in the review being prepared: what the next publish sends, as far as its comments go. */
  includedCount: number = $derived(this.reviewDrafts.filter(isIncluded).length);

  /** Drafts not included: they stay drafts until the viewer ticks them. */
  notIncludedCount: number = $derived(this.reviewDrafts.length - this.includedCount);

  unresolvedCount: number = $derived(
    this.threads.filter((t) => !t.isResolved && t.comments.nodes.some((c) => c.pullRequestReview?.state !== 'PENDING'))
      .length,
  );

  /** Conversation drafts (lines outside the diff). */
  heldCount: number = $derived(this.held.length);

  /**
   * What "Request changes" can be tied to: the open published threads, and
   * the new threads (line and file drafts) a publish would send now. A
   * draft that is not included is not published, so it does not count.
   */
  actionableCount: number = $derived(
    this.unresolvedCount + this.planFor('REQUEST_CHANGES', '').inline.filter((d) => d.target.kind === 'line' || d.target.kind === 'file').length,
  );

  /** Titles of the issues and pull requests related to this one (`loadKnownRefs`); empty until read, or when they cannot be. */
  get knownRefs(): KnownRef[] {
    return valueOf(this.st.refs) ?? NO_REFS;
  }

  /** Deployments of the pull request's latest commits; empty until read, or when they cannot be. */
  get deployments(): DeploymentCommit[] {
    return valueOf(this.st.deployments) ?? NO_DEPLOYMENTS;
  }

  /** Preview deployments: GitHub's, and those bots link in the conversation (logic/previews.ts), ready ones first. */
  previews: Preview[] = $derived.by(() => {
    const head = this.shown.pr?.headRefOid;
    if (!head) return [];
    const timeline = this.shown.timeline;
    const author = this.shown.pr?.author?.login ?? null;
    // Read from GitHub's deployments and from what bots wrote: never worth the page (see runtime/total.ts).
    return total('the preview deployments', NO_PREVIEWS, () => previews(this.deployments, timeline, head, headPushedAt(timeline, head), author));
  });

  /** Commits head force-pushes replaced, for the track (null until looked up, or when that failed). */
  get replaced(): ReplacedLookup | null {
    return this.st.replaced;
  }

  checks: CheckContext[] = $derived(this.pr?.commits.nodes[0]?.commit.statusCheckRollup?.contexts.nodes ?? []);

  /** All check contexts GitHub reports for the head (may exceed `checks.length`). */
  checksTotal: number = $derived(
    this.pr?.commits.nodes[0]?.commit.statusCheckRollup?.contexts.totalCount ?? this.checks.length,
  );

  checksState: string | null = $derived(this.pr?.commits.nodes[0]?.commit.statusCheckRollup?.state ?? null);

  /** Files GitHub did not list (its pulls/:n/files stops at 3,000). */
  filesMissing: number = $derived(
    this.files && this.pr ? Math.max(0, this.pr.changedFiles - this.files.length) : 0,
  );

  author: string = $derived(this.pr?.author?.login ?? '');

  isAuthor: boolean = $derived(!!this.viewer && this.viewer === this.author);

  /** Writes to GitHub fail here: the App is not installed on the repository, or lacks Pull requests: write. */
  readOnly: boolean = $derived(this.access === 'not_installed' || this.access === 'no_permission');

  /**
   * The changelist server said no installation it knows of covers the
   * repository (403 `not_installed` on its own data, or a revocation with
   * that reason). That is the server's record, which can lag GitHub's, so
   * it turns off only server data, never GitHub writes. It stays for the
   * page's life: the server data was dropped (see `notInstalled`).
   */
  get unrecorded(): boolean {
    return this.st.unrecorded;
  }
  set unrecorded(unrecorded: boolean) {
    this.put({ unrecorded });
  }

  /**
   * Whether the changelist server keeps data for this repository (marks,
   * review drafts, attention overrides): not once the viewer lost access,
   * nor where no installation of the App covers it. Every draft is server
   * data, so while it is off no draft is saved, changed, imported or
   * published.
   */
  serverData: boolean = $derived(serverData(this.st));

  /** Why server data (review marks, drafts, attention changes) is off; null while it is on. */
  get serverDataText(): string | null {
    if (this.access === 'not_installed') return this.readOnlyText;
    if (this.unrecorded)
      return `Changeling can't reach ${this.owner}/${this.repo} through an installation it knows about, so it keeps no review marks, drafts or attention changes here. If you just installed it, reload.`;
    return null;
  }

  /** Why writes are off (the read-only banner and the disabled controls' tooltip); null while they are not. */
  get readOnlyText(): string | null {
    return readOnlyText(this.owner, this.repo, this.access);
  }

  /**
   * Draft controls are off: GitHub writes are (a draft could never be
   * published), or server data is (drafts are held by the server).
   */
  draftsOff: boolean = $derived(this.readOnly || !this.serverData);

  /** Why draft controls are off (their tooltip); null while they are on. */
  get draftsOffText(): string | null {
    return this.readOnlyText ?? this.serverDataText;
  }

  /**
   * The PR page's banner: why writes are off (with the App's installation
   * link), why server data is off (with a reload), or both, each said once;
   * null while neither is.
   */
  get banner(): { text: string[]; install: boolean; reload: boolean } | null {
    const text: string[] = [];
    if (this.readOnlyText) text.push(this.readOnlyText);
    // `not_installed` from GitHub says both at once (see `serverDataText`).
    const unrecorded = this.unrecorded && this.access !== 'not_installed';
    if (unrecorded && this.serverDataText) text.push(this.serverDataText);
    if (text.length === 0) return null;
    return { text, install: this.readOnly, reload: unrecorded };
  }

  /** The App's installation page (null when the App's slug is unknown). */
  installLink: string | null = $derived(installUrl(session.config?.app_slug, session.config?.github_web_url));

  /** Who the viewer is to the attention override rule. */
  attentionWriter: AttentionWriter = $derived({
    viewer: session.user?.login ?? this.viewer,
    author: this.author,
    permission: this.repository?.viewerPermission ?? null,
  });

  /** The viewer may change other people's attention (triage or more, or the author). */
  canSetOthersAttention: boolean = $derived(this.serverData && canSetOthers(this.attentionWriter));

  /** People who can be put in the attention set (author, reviewers, requested, commenters). */
  participants: Participant[] = $derived(this.pr ? participantsOf(this.pr, this.threads, this.timeline) : []);

  // ---------------------------------------------------------------- loading

  /**
   * Fetch the PR (metadata, every thread and comment, timeline, checks).
   * A load supersedes the one in flight, so the newest read is the one
   * shown, and a slow earlier load never replaces newer state; it keeps
   * checks that a checks refresh read after it began. A load that fails
   * because GitHub calls are rate limited runs again when the pause ends.
   * Settles once this load, or one that superseded it, has answered.
   */
  load(): Promise<void> {
    if (this.disposed) return Promise.resolve();
    // Subscribe before the first fetch so changes made while it runs are announced.
    this.subscribe();
    this.program.send({ t: 'load' });
    const basis = this.st.reads;
    return this.when((s) => s.prDone >= basis || s.reads < basis);
  }

  /**
   * Look up the commits head force-pushes replaced (`replaced`): once per
   * head and set of force-pushes. A failure leaves what the timeline shows.
   */
  loadReplaced(): void {
    this.program.send({ t: 'replaced' });
  }

  /** Refetch only the head's checks (after a check/status notice). */
  refreshChecks(): Promise<void> {
    if (!this.pr || this.disposed) return Promise.resolve();
    this.program.send({ t: 'checks' });
    const basis = this.st.checksRun?.basis;
    return basis === undefined ? Promise.resolve() : this.when((s) => s.checksDone >= basis || s.reads < basis);
  }

  /**
   * Refetch checks after a check notice: at most one refetch per
   * CHECKS_COALESCE_MS, and one more after a refetch that notices arrived
   * during.
   */
  refreshChecksSoon() {
    this.program.send({ t: 'checks-soon' });
  }

  /**
   * Load the changed files for the current head. Without `force`, files
   * already loaded for this head are kept. Single-flight per head: a call
   * while a load for the same head runs joins it; a load for another head
   * supersedes it, and its callers wait for the newer one, so they never
   * see files missing.
   */
  loadFiles(force = false): Promise<void> {
    this.program.send({ t: 'files', force });
    return this.when((s) => s.filesRun === null);
  }

  /** Ask whether the App covers this repository (answers are cached for the page's life; see `api/install`). */
  checkAccess(): Promise<void> {
    this.program.send({ t: 'access' });
    const run = this.st.accessRuns;
    return this.when((s) => s.accessDone >= run);
  }

  /** Compute the attention set from what the server sent (pending changes are laid over it like every write). */
  computeAttention(): Promise<void> {
    this.program.send({ t: 'attention' });
    const run = this.st.attentionRuns;
    return this.when((s) => s.attentionDone >= run);
  }

  /**
   * Read the attention overrides again and work the attention set out from
   * them; the read confirms the attention changes accepted before it began
   * (see `changeAttention`). A failed read works it out from the overrides
   * the server sent before.
   */
  private loadOverrides(): Promise<void> {
    this.program.send({ t: 'overrides' });
    const basis = this.st.overridesReads;
    return this.when((s) => s.overridesDone >= basis);
  }

  latestEventAt(): string {
    const { pr, timeline } = this.server;
    if (!pr) return new Date().toISOString();
    return latestEventAt(toTimelineEvents(timeline, { author: this.author, createdAt: pr.createdAt }));
  }

  /** Load the viewer's drafts (the latest request wins; a read a draft write overtook is run again). */
  loadDrafts(): Promise<void> {
    this.program.send({ t: 'drafts' });
    return this.when((s) => !s.draftsRun);
  }



  /** Refetch (debounced) after an invalidation notice, reconnect or focus. */
  refreshSoon(files = false) {
    this.program.send({ t: 'refresh', files });
  }

  /**
   * The user's "Viewed" setting, for this pull request: off stops the sync
   * at once (writes not sent yet are dropped; one in flight finishes); on
   * reconciles the files with GitHub's states, reading them first when the
   * last load did not.
   */
  setViewedSync(on: boolean) {
    if (this.viewed.enabled === on) return;
    this.viewed.setEnabled(on);
    if (on && !this.viewedRead) this.refreshSoon();
  }

  /** Topics this store listens on. */
  topics(): string[] {
    const repo = `${this.owner}/${this.repo}`.toLowerCase();
    const topics = [`pr:${repo}#${this.number}`, `repo:${repo}`];
    if (session.user) topics.push(`user:${session.user.id}`);
    return topics;
  }

  /** No view shows the store (see `releasePrStore`): it does not listen for notices. */
  private asleep = false;

  /** No view shows it any more: stop listening for notices, while its writes finish. */
  sleep() {
    this.asleep = true;
    this.live?.close();
    this.live = null;
  }

  /** A view shows it again: listen again (opening refetches what was missed). */
  wake() {
    if (!this.asleep) return;
    this.asleep = false;
    if (this.server.pr) this.subscribe();
  }

  private subscribe() {
    if (this.live || this.disposed || this.asleep) return;
    this.live = new LiveEvents(
      this.topics(),
      (topic, notice) => this.onNotice(topic, notice),
      (topic, reason) => this.onRevoked(topic, reason),
    );
  }

  /**
   * The server stopped a topic of this repository. Without access, the
   * store stops (as if disposed) and drops everything it showed, so the
   * view says so instead of showing stale data. Without an installation,
   * GitHub still shows the PR: it becomes read-only, and what the server
   * kept for it (marks, drafts, overrides) is dropped.
   */
  onRevoked(topic: string, reason: RevokeReason) {
    const repo = `${this.owner}/${this.repo}`.toLowerCase();
    if (topic !== `repo:${repo}` && !topic.startsWith(`pr:${repo}#`)) return;
    if (reason === 'not_installed') return this.notInstalled();
    this.program.send({ t: 'revoked' });
    // The Program stays for the writes in flight (their answers are still told); it loads nothing more.
    this.halt();
  }

  /**
   * The server knows no installation covering the repository (a revoked
   * topic, or its 403 `not_installed` on its own data): server data turns
   * off (`unrecorded`), said once by a banner rather than as failures to
   * load, and what the server kept for it is dropped.
   */
  private notInstalled() {
    this.program.send({ t: 'not-installed' });
  }

  /** Handle an SSE notice, or a trigger (open/reconnect/resync/focus) when `notice` is null. */
  onNotice(topic: string, notice: Notice | null) {
    if (this.disposed) return;
    const lastLoadAt = this.st.lastLoadAt;
    if (!notice) {
      const trigger = topic as Trigger;
      const files = trigger !== 'focus' && trigger !== 'open';
      if (shouldRefetch(trigger, lastLoadAt)) this.refreshSoon(files);
      else if (pausedForRateLimit()) this.refetchAfterRateLimit(files);
      else return;
      void this.marks.load();
      void this.notes.reload();
      void this.loadDrafts();
      return;
    }
    const fromRepo = topic.startsWith('repo:');
    switch (notice.kind) {
      case 'preferences':
        // The user changed a setting on another page of theirs.
        settings.refresh();
        return;
      // Marks and attention overrides come from the changelist server, not GitHub.
      case 'marks':
        // The user topic carries marks notices for every repository; the
        // store reloads what it shows of what changed.
        if (this.isThisRepo(notice)) void this.marks.onNotice(notice as MarksNotice);
        return;
      case 'flags':
        if (this.isThisRepo(notice)) void this.marks.onNotice(notice as MarksNotice);
        return;
      case 'held_comments':
        // Also on the user topic, for every PR.
        if (this.isThisPr(notice)) void this.loadDrafts();
        return;
      case 'notes':
        // On the user topic, for every repository: notes belong to none of its pull requests.
        if (typeof notice.repo === 'string' && notice.repo.toLowerCase() === `${this.owner}/${this.repo}`.toLowerCase()) void this.notes.heard(notice);
        return;
      case 'attention':
        void this.loadOverrides();
        return;
    }
    // Check and status events name a commit, not a PR: keep those for our head.
    // The repo topic carries every PR's other notices: keep only ours.
    const ours =
      notice.kind === 'checks'
        ? !notice.head_sha || notice.head_sha === this.pr?.headRefOid
        : !fromRepo || notice.number === this.number;
    if (!ours) return;
    if (!shouldRefetch('notice', lastLoadAt)) this.refetchAfterRateLimit(notice.kind === 'pr');
    else if (notice.kind === 'checks') this.refreshChecksSoon();
    else this.refreshSoon(notice.kind === 'pr');
  }

  /** A marks or flags notice: marks are the repository's, whichever view wrote them. */
  private isThisRepo(notice: Notice): boolean {
    return typeof notice.repo === 'string' && notice.repo.toLowerCase() === `${this.owner}/${this.repo}`.toLowerCase();
  }

  private isThisPr(notice: Notice): boolean {
    return (
      typeof notice.repo === 'string' &&
      notice.repo.toLowerCase() === `${this.owner}/${this.repo}`.toLowerCase() &&
      notice.number === this.number
    );
  }

  /**
   * Notices were dropped while GitHub calls are paused for the rate limit:
   * refetch once when the pause ends (the Program's `resume` retry).
   */
  private refetchAfterRateLimit(files: boolean) {
    this.program.send({ t: 'paused', files, until: null });
  }

  /** Stop listening and syncing; nothing loads from now on. */
  private halt() {
    this.disposed = true;
    this.live?.close();
    this.live = null;
    this.marks.dispose();
    this.releaseNotes();
    this.viewed.dispose();
  }

  dispose() {
    this.halt();
    this.stopped = true;
    // Everything in flight is dropped, and every timer with it.
    this.program.dispose();
    this.unhear();
  }

  // ---------------------------------------------------------------- writes
  //
  // Every write goes through `writes` (see pending.svelte.ts): it shows at
  // once, stays marked pending until confirmed, and rolls back with a Retry
  // when it fails. The methods resolve true once the server accepted the
  // write, false when it failed. `draft` names the saved text the write
  // sends (see drafts.ts): deleted on success, kept on failure.

  /** Confirmed by the PR's next load, which supersedes any in flight (they may predate the write). */
  private readonly byPrLoad: Confirm<Reloaded, unknown> = {
    reload: 'pr',
    ticket: () => this.st.reads,
    run: async () => {
      await this.load();
      // A store that stopped (revoked) never loads again: what it accepted is done.
      if (this.disposed) this.writes.settle('pr', Infinity);
    },
  };

  /** A write confirmed by the PR's next load. */
  private write<T = void>(spec: Omit<WriteSpec<PrState, Reloaded, Subject, T>, 'confirm'>): Promise<boolean> {
    return this.writes.run<T>({ ...spec, confirm: this.byPrLoad });
  }

  /** The viewer as the author of a comment shown before it is saved. */
  private me(): { login: string; avatarUrl?: string } | null {
    const login = this.viewer || session.user?.login;
    return login ? { login, avatarUrl: session.user?.avatar_url } : null;
  }

  /**
   * What a failed write tells the user. GitHub's "Resource not accessible
   * by integration" (the App is not installed here, or lacks a permission)
   * becomes an explanation with the App's installation link, and marks the
   * repository read-only, unless the App's installation is known to cover it.
   */
  writeError(e: unknown): WriteError {
    // The changelist server's own record (a draft write): server data turns off, GitHub writes stay.
    if (isNotInstalled(e)) {
      this.notInstalled();
      return { text: this.serverDataText ?? message(e), link: null };
    }
    const w = describeWriteError(e, {
      owner: this.owner,
      repo: this.repo,
      appSlug: session.config?.app_slug,
      webUrl: session.config?.github_web_url,
    });
    if (!w) return { text: message(e), link: null };
    // The App's installation was found covering the repository with Pull
    // requests: write: GitHub refused this one write for another reason (a
    // permission that write needs, or the viewer's own), so it says why and
    // the pull request stays writable.
    if (this.access === 'covered') return { text: `GitHub refused this: ${message(e)}`, link: null };
    if (!this.readOnly) this.access = 'not_installed';
    return w;
  }

  /** Commit new comments anchor to: the diff on screen, else the head ('' before the pull request has loaded, when no draft is made). */
  private commentCommit(): string {
    return this.diffHead ?? this.server.pr?.headRefOid ?? '';
  }

  /** The Files view shows `owner`'s diff, built for `head` (null until known). */
  showDiff(owner: object, head: string | null) {
    this.diffOwner = owner;
    this.diffHead = head;
  }

  /** `owner`'s diff left the screen; comments anchor to the head again. */
  hideDiff(owner: object) {
    if (this.diffOwner !== owner) return;
    this.diffOwner = null;
    this.diffHead = null;
  }

  // ---------------------------------------------------------------- drafts
  //
  // Review drafts live on the changelist server until published (GitHub's
  // pending review would post every comment in it, including those still
  // to review). Each is a write of its own, landed with the server's answer.

  /** The draft shown in a review thread as comment `commentId`, or null (a comment GitHub has). */
  draftOfComment(commentId: string): ReviewDraft | null {
    const id = draftIdOf(commentId, this.shownDrafts);
    return id === null ? null : (this.shownDrafts.find((d) => d.id === id) ?? null);
  }

  /** The viewer already answered open thread `t` with Done or Ack, as a draft that will be published (see `resolvedBy`). */
  quickReplied(t: ReviewThread): boolean {
    return !t.isResolved && this.shownDrafts.some((d) => answeredBy(d) === t.id);
  }

  /** The head commit a draft is included on now; null until the pull request has loaded. */
  private head(): string | null {
    return this.shown.pr?.headRefOid ?? null;
  }

  // The writes themselves are in pr/draftWrites.ts (drafts), pr/prWrites.ts
  // (GitHub and attention) and pr/publish.svelte.ts (publish and hand-off).

  /** A draft comment on lines of the diff (a new review thread when published). Shown at once, on those lines. */
  addThread(t: NewThread, draft: Draft) {
    return this.drafting.addThread(t, draft);
  }
  /** A file-level draft: a file-level review thread when published. */
  addFileThread(path: string, body: string, draft: Draft) {
    return this.drafting.addFileThread(path, body, draft);
  }
  /** A draft on a file the PR does not change: posted to the conversation when published. */
  addHeld(c: NewHeld, draft: Draft) {
    return this.drafting.addHeld(c, draft);
  }
  /** A private note promoted to a draft: shown at once while `send` (the note's promote) is answered (pr/draftWrites.ts). */
  promoteNote(noteId: number, target: DraftTarget, body: string, send: () => Promise<ReviewDraft>) {
    return this.drafting.promoteNote(noteId, target, body, send);
  }
  /** A draft on the whole pull request: sent in the review's body when published (pr/draftWrites.ts). */
  addPrComment(body: string, draft: Draft) {
    return this.drafting.addPrComment(body, draft);
  }

  /** Edit a draft's text; its state stays. Shown at once. */
  editDraft(id: ReviewDraft['id'], body: string, draft: Draft) {
    return this.drafting.editDraft(id, body, draft);
  }
  /** Include a draft in the review being prepared (the next publish sends it), or take it out (it stays a draft). Only the latest choice is sent. */
  includeDraft(id: ReviewDraft['id'], on: boolean) {
    // Including a draft set aside brings it back to the review.
    if (on && this.isAside(id)) this.setAside(id, false);
    return this.drafting.includeDraft(id, on);
  }
  /** Whether draft `id` is set aside (review/asides.ts). */
  isAside(id: ReviewDraft['id']): boolean {
    return this.asides.has(String(id));
  }
  /**
   * Set draft `id` aside, kept for the viewer and out of the review (taken
   * out of it, if it was included), or bring it back. Private to the
   * viewer, kept with their review marks; shown at once.
   */
  setAside(id: ReviewDraft['id'], aside: boolean) {
    if (aside && this.shownDrafts.some((d) => d.id === id && isIncluded(d))) void this.drafting.includeDraft(id, false);
    this.marks.apply(planAside(id, this.marks.flags, aside));
  }
  /** Discard a draft; `editKey`: its edit box's saved text, which goes with it; `quiet`: the caller tells the user itself. */
  discardDraft(id: ReviewDraft['id'], editKey?: string, opts: { quiet?: boolean; onGitHub?: (found: DiscardedOnGitHub) => void } = {}) {
    return this.drafting.discardDraft(id, editKey, opts);
  }
  /** Bring back a draft discarded a moment ago (Undo), as it was. */
  restoreDraft(d: ReviewDraft) {
    return this.drafting.restoreDraft(d);
  }
  /** Read the viewer's pending review on GitHub and mirror it in the drafts; nothing is written to GitHub. */
  mirrorDrafts(): Promise<boolean> {
    return this.drafting.mirrorDrafts();
  }
  /** A draft reply on review thread `threadId`, shown at once at its end. */
  reply(threadId: string, body: string, draft: Draft) {
    return this.drafting.reply(threadId, body, draft);
  }
  /** Done / Ack: add the word as a draft reply; the thread is resolved when the review is published. */
  quickReply(threadId: string, word: 'Done' | 'Ack') {
    return this.drafting.quickReply(threadId, word);
  }
  /** Delete a draft shown in a review thread (one of changelist's is discarded; a pending review's comment is deleted on GitHub). */
  deleteDraftComment(commentId: string) {
    return this.drafting.deleteDraftComment(commentId);
  }
  /** Discard every draft, and a pending review made on github.com if there is one. */
  discardAllDrafts(): Promise<boolean> {
    return this.drafting.discardAllDrafts();
  }
  /** Resolve or unresolve a thread: shown at once, the thread marked as being resolved until GitHub confirms. */
  setResolved(threadId: string, resolved: boolean) {
    return this.sending.setResolved(threadId, resolved);
  }
  /** A conversation comment, shown at once at the end of the conversation. */
  addIssueComment(body: string, draft: Draft) {
    return this.sending.addIssueComment(body, draft);
  }
  /** Merge; the PR is not shown merged before GitHub says so. */
  merge(method: 'MERGE' | 'SQUASH' | 'REBASE') {
    return this.sending.merge(method);
  }
  /** Ask `login` to review (again). */
  requestReviewer(login: string, how?: HandOffWrite) {
    return this.sending.requestReviewer(login, how);
  }
  setAttention(login: string, state: 'add' | 'remove' | null) {
    return this.sending.setAttention(login, state);
  }
  /** Write attention overrides, one write per login, each shown at once and confirmed by the next fetch of the overrides. */
  changeAttention(changes: AttentionChange[], how?: HandOffWrite): Promise<boolean> {
    return this.sending.changeAttention(changes, how);
  }
  /** Publish exactly `plan` (from `planFor`): the review with its inline drafts, then the conversation drafts. */
  publish(plan: PublishPlan, draft?: Draft): Promise<PublishResult> {
    return this.publishing.publish(plan, draft);
  }
  /** Publish `plan` and, once it is in, apply `after`; answers as soon as the publish is in, `handOff` settles once the hand-off has. */
  publishWithHandOff(plan: PublishPlan, draft: Draft | undefined, after: AfterPublish): Promise<HandOffResult> {
    return this.publishing.publishWithHandOff(plan, draft, after);
  }
  /** The Review view was left: the last publish's error and the finished hand-off writes are forgotten. */
  leftPublish() {
    this.publishing.leftPublish();
  }
  /** Send again the hand-off writes that failed. */
  retryHandOff(): Promise<boolean> {
    return this.publishing.retryHandOff();
  }
  /** Post only the conversation drafts (Retry after some failed to post): never submits a review. */
  publishHeld(): Promise<PublishResult> {
    return this.publishing.publishHeld();
  }

  /** Threads the next publish resolves: an included Done or Ack reply answers them (see `publishPlan`). */
  resolveOnPublish: string[] = $derived(this.planFor('COMMENT', '').resolve);

  /**
   * What publishing with `event` and summary `body` would publish (see
   * `publishPlan`): the Review view's preview and the publish read the same plan.
   */
  planFor(event: ReviewEvent, body: string, drafts: readonly ReviewDraft[] = this.shownDrafts): PublishPlan {
    return publishPlan({
      drafts,
      snapshots: this.snapshots,
      head: this.head() ?? '',
      fallbackCommit: this.commentCommit(),
      verdict: event,
      body,
      canResolve: (id) => this.shown.threads.some((t) => t.id === id && t.viewerCanResolve),
    });
  }

  /** Whether the viewer may write the override for `login` (the server's rule; see `logic/attention`). */
  /** Attention overrides are server data: none while it is off (see `serverData`). */
  canSetAttention(login: string): boolean {
    return this.serverData && canSetAttention(login, this.attentionWriter);
  }

}

export { mergeBlockers } from '../logic/readiness';

/**
 * The verdict to keep selected on the Review view: `event` while it is
 * allowed, else Comment (no verdict): a verdict is never taken up for the
 * reviewer, so Request changes going away does not become an Approve.
 */
export function availableVerdict(event: ReviewEvent, canApprove: boolean, canRequestChanges: boolean): ReviewEvent {
  if (event === 'APPROVE' && !canApprove) return 'COMMENT';
  if (event === 'REQUEST_CHANGES' && !canRequestChanges) return 'COMMENT';
  return event;
}

/**
 * A store and the views holding it. `root` owns the store's reactive state
 * (its `$derived`s), so that state belongs to no view: it outlives the view
 * that first acquired it. `linger` stops waiting for a released store's
 * writes to finish.
 */
interface Held {
  store: PrStore;
  refs: number;
  root: () => void;
  linger: (() => void) | null;
}

const stores = new Map<string, Held>();

function storeKey(owner: string, repo: string, number: number) {
  return `${owner}/${repo}#${number}`.toLowerCase();
}

/**
 * The shared store for a PR, loading it on first acquire. Every acquire must
 * be paired with `releasePrStore` (PrView does so on mount/destroy). A store
 * released with writes in flight is the one acquired again (see
 * `releasePrStore`).
 */
export function acquirePrStore(owner: string, repo: string, number: number): PrStore {
  const key = storeKey(owner, repo, number);
  let e = stores.get(key);
  if (!e) {
    // Built in a root of its own: inside the acquiring view's effect, its deriveds would die with that view.
    let store!: PrStore;
    const root = $effect.root(() => {
      store = new PrStore(owner, repo, number);
    });
    e = { store, refs: 0, root, linger: null };
    stores.set(key, e);
    void e.store.load();
  }
  e.linger?.();
  e.linger = null;
  e.store.wake();
  e.refs++;
  return e.store;
}

/**
 * Release a store; the last release disposes it (closes SSE, flushes marks).
 * While a write is in flight (`writes.active`) the store is kept, not
 * listening for notices, and disposed once nothing is: the request and the
 * text it took outlive the view, so a view coming back must find the same
 * writes (what they hold, their markers). A failure left when it is
 * disposed is not lost: its text is in its box's outbox, which the box's
 * next load brings back.
 *
 * Settles once this release has disposed the store (at once when nothing
 * was in flight, or when it was not the last release); it never settles
 * if a view acquires the store again before its writes are done.
 */
export function releasePrStore(store: PrStore): Promise<void> {
  const key = storeKey(store.owner, store.repo, store.number);
  const e = stores.get(key);
  if (!e || e.store !== store) return Promise.resolve();
  if (--e.refs > 0) return Promise.resolve();
  const held = e;
  const drop = () => {
    stores.delete(key);
    try {
      store.dispose();
    } finally {
      held.root();
    }
  };
  if (!store.writes.active) {
    drop();
    return Promise.resolve();
  }
  store.sleep();
  return new Promise<void>((dropped) => {
    const stop = store.writes.whenIdle(() => {
      stop();
      held.linger = null;
      drop();
      dropped();
    });
    held.linger = stop;
  });
}

/** Stores currently alive (for tests and diagnostics). */
export function liveStoreCount(): number {
  return stores.size;
}
